import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { BusinessArea, ApiResponse } from '@/types'

// Fetch all business areas
export function useBusinessAreas() {
  return useQuery({
    queryKey: ['business-areas'],
    queryFn: async (): Promise<BusinessArea[]> => {
      const res = await fetch('/api/business-areas')
      const data: ApiResponse<BusinessArea[]> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to fetch business areas')
      return data.data!
    },
  })
}

// Create business area
export function useCreateBusinessArea() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (area: { name: string; display_order?: number }) => {
      const res = await fetch('/api/business-areas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(area),
      })
      const data: ApiResponse<BusinessArea> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to create business area')
      return data.data!
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-areas'] })
    },
  })
}

// Update business area
export function useUpdateBusinessArea() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...area }: { id: string; name?: string; display_order?: number; is_active?: boolean }) => {
      const res = await fetch(`/api/business-areas/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(area),
      })
      const data: ApiResponse<BusinessArea> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to update business area')
      return data.data!
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-areas'] })
      queryClient.invalidateQueries({ queryKey: ['metrics'] })
      queryClient.invalidateQueries({ queryKey: ['scorecard'] })
    },
  })
}

// Delete business area
export function useDeleteBusinessArea() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/business-areas/${id}`, { method: 'DELETE' })
      const data: ApiResponse<void> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to delete business area')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-areas'] })
      queryClient.invalidateQueries({ queryKey: ['metrics'] })
      queryClient.invalidateQueries({ queryKey: ['scorecard'] })
    },
  })
}
